import { gql, NetworkStatus, useQuery } from '@apollo/client'

import { useGetNewestData } from '@/clients/utoniq-core'
import {
  CampaignBoxFragmentDoc,
  CampaignsConnectionFragmentDoc,
  CurrentUserFragmentDoc,
  EntryCampaignRankingConnectionFragmentDoc,
  FetchNewGlobalEventsQuery,
  FetchNewGlobalEventsQueryVariables,
  GlobalEventsQuery,
  GlobalEventsQueryVariables,
  MissionBoxFragmentDoc,
  MissionsConnectionFragmentDoc,
  MissionType,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import { useCurrentUser } from '@/core'
import { makeViewModel } from '@/core/architecture'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { userClaims } = useCurrentUser()

  const { isFirstLoadRef } = useGetNewestData<
    FetchNewGlobalEventsQuery,
    FetchNewGlobalEventsQueryVariables
  >({
    query: FETCH_NEW_EVENT,
    variables: { typeFilter: [MissionType.Quiz, MissionType.ItemCollect] },
    fields: [
      {
        fieldName: 'organization.campaigns',
        fragmentName: 'CampaignBox',
        fragment: CampaignBoxFragmentDoc,
      },
      {
        fieldName: 'organization.missions',
        fragmentName: 'MissionBox',
        fragment: MissionBoxFragmentDoc,
      },
    ],
  })

  const { data, fetchMore, networkStatus } = useQuery<
    GlobalEventsQuery,
    GlobalEventsQueryVariables
  >(QUERY, {
    variables: {
      typeFilter: [MissionType.Quiz, MissionType.ItemCollect],
    },
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    onCompleted() {
      isFirstLoadRef.current = false
    },
  })

  const isNextFetch = !!data

  return {
    state: {
      loading: networkStatus === NetworkStatus.loading && !isNextFetch,
      fetching: networkStatus === NetworkStatus.fetchMore,
      hasMissionNextPage: !!data?.organization?.missions?.pageInfo.hasNextPage,
      hasCampaignNextPage:
        !!data?.organization?.campaigns?.pageInfo.hasNextPage,
      missions: data?.organization?.missions ?? null,
      campaigns: data?.organization?.campaigns ?? null,
      campaignsParticipating: data?.organization?.entryCampaignRankings ?? null,
      user: data?.user ?? null,
      isArtist: !!userClaims?.orgs?.[config.organizationId]?.artist,
      followArtists: data?.user?.organizationFollowees ?? null,
      memberArtists: data?.user?.organizationPatrons ?? null,
    },
    actions: {
      async onFetchMoreMission() {
        await fetchMore({
          variables: {
            missionsPageInput: {
              cursor: data?.organization?.missions?.pageInfo.endCursor,
            },
          },
        })
      },
      async onFetchMoreCampaign() {
        await fetchMore({
          variables: {
            campaignsPageInput: {
              cursor: data?.organization?.campaigns?.pageInfo.endCursor,
            },
          },
        })
      },
    },
  }
})

const QUERY = gql`
  query GlobalEvents(
    $typeFilter: [MissionType!]
    $missionsPageInput: PageInput
    $campaignsPageInput: PageInput
  ) {
    organization {
      id
      campaigns(pageInput: $campaignsPageInput) {
        ...CampaignsConnection
      }
      entryCampaignRankings {
        ...EntryCampaignRankingConnection
      }
      missions(typeFilter: $typeFilter, pageInput: $missionsPageInput) {
        ...MissionsConnection
      }
    }
    user {
      ...CurrentUser
    }
  }
  ${CampaignsConnectionFragmentDoc}
  ${MissionsConnectionFragmentDoc}
  ${EntryCampaignRankingConnectionFragmentDoc}
  ${CurrentUserFragmentDoc}
`

const FETCH_NEW_EVENT = gql`
  query FetchNewGlobalEvents(
    $typeFilter: [MissionType!]
    $missionsPageInput: PageInput
    $campaignsPageInput: PageInput
  ) {
    organization {
      id
      campaigns(pageInput: $campaignsPageInput) {
        ...CampaignsConnection
      }
      missions(typeFilter: $typeFilter, pageInput: $missionsPageInput) {
        ...MissionsConnection
      }
    }
  }
  ${CampaignsConnectionFragmentDoc}
  ${MissionsConnectionFragmentDoc}
  ${EntryCampaignRankingConnectionFragmentDoc}
`
